import { round } from "lodash-es";

import { FeatureName } from "@/common/constants/featureName";
import { AppArea } from "@/common/constants/routing";
import { featureManager } from "@/config/features";
import {
  OnboardingDto,
  OnboardingStatus,
  OnboardingStepDefinitionDto,
  OnboardingStepDto,
} from "@/core/api/generated/v1";

import { RoutingHelper } from "../routing";

export interface OnboardingStepPair {
  definition: OnboardingStepDefinitionDto;
  step: OnboardingStepDto | null;
}

export interface OnboardingProgress {
  completedStepCount: number;
  totalStepCount: number;
  progressPct: number;
  progressPct100: number;
}

export class OnboardingHelper {
  public static isEnabled(params?: { url?: string; pathname?: string }): boolean {
    return (
      featureManager.isEnabled(FeatureName.Onboarding) &&
      RoutingHelper.isAppAreaActive({ area: AppArea.Fleet, ...params })
    );
  }

  public static getStepPairs(onboarding: OnboardingDto | null): OnboardingStepPair[] | null {
    return (
      onboarding?.definition?.steps?.map((stepDefinition) => ({
        definition: stepDefinition,
        step: onboarding?.steps?.find((step) => step.definitionId === stepDefinition.id) || null,
      })) || null
    );
  }

  public static getCurrentStepPair(params: {
    onboarding: OnboardingDto | null;
    currentStepDefinitionId?: string | null;
  }): OnboardingStepPair | null {
    const pairs = this.getStepPairs(params.onboarding);
    return (
      pairs?.find(
        (x) =>
          x.definition.id === params.currentStepDefinitionId ||
          (x.definition.isEnabled &&
            (!x.step ||
              x.step.status === OnboardingStatus.InProgress ||
              x.step.status === OnboardingStatus.NotStarted)),
      ) || null
    );
  }

  public static getNextStepPair(params: {
    onboarding: OnboardingDto | null;
    currentStepDefinitionId?: string | null;
  }): OnboardingStepPair | null {
    const pairs = this.getStepPairs(params.onboarding);
    const currentStepDefinitionIndex = params.currentStepDefinitionId
      ? (pairs?.findIndex((x) => x.definition.id === params.currentStepDefinitionId) ?? -1)
      : -1;
    return (
      pairs?.find(
        (x, i) =>
          x.definition.isEnabled &&
          x.definition.id !== params.currentStepDefinitionId &&
          i > currentStepDefinitionIndex &&
          (!x.step ||
            x.step.status === OnboardingStatus.InProgress ||
            x.step.status === OnboardingStatus.NotStarted),
      ) || null
    );
  }

  public static getProgress(onboarding: OnboardingDto | null): OnboardingProgress | null {
    const stepPairs = this.getStepPairs(onboarding)?.filter((x) => x.definition.isEnabled);
    if (!stepPairs) {
      return null;
    }

    const completedStepCount = stepPairs.filter(
      (pair) => pair.step?.status === OnboardingStatus.Completed,
    ).length;
    const totalStepCount = stepPairs.length;
    const progressPct = totalStepCount > 0 ? round(completedStepCount / totalStepCount, 2) : 0;

    return {
      completedStepCount,
      totalStepCount,
      progressPct,
      progressPct100: Math.round(progressPct * 100),
    };
  }
}
